import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  FlaskConical, Play, Square, Trash2, Settings, RotateCcw,
  TrendingUp, TrendingDown, BarChart3, Target, ChevronDown, ChevronUp,
  Plus, Loader2, X, Trophy, Radio, ArrowDown, ArrowUp, Coins
} from 'lucide-react';

const RISK_COLORS = {
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  high: 'bg-red-500/20 text-red-400 border-red-500/40',
};

const PAIR_COLORS = {
  BTCUSD: 'bg-orange-500/15 text-orange-400 border-orange-500/40',
  ETHUSD: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
  SOLUSD: 'bg-purple-500/15 text-purple-400 border-purple-500/40',
  XRPUSD: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
  DOTUSD: 'bg-pink-500/15 text-pink-400 border-pink-500/40',
  LINKUSD: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/40',
};

const TestBotFolder = () => {
  const [bots, setBots] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBot, setExpandedBot] = useState(null);
  const [settingsBot, setSettingsBot] = useState(null);
  const [settingsForm, setSettingsForm] = useState({ max_daily_loss: 5, stop_loss_percentage: 2, risk_per_trade: 0.01 });
  const [creating, setCreating] = useState(false);
  const [botTrades, setBotTrades] = useState({});
  const [promoteBot, setPromoteBot] = useState(null);
  const [promoteName, setPromoteName] = useState('');
  const [promoteTarget, setPromoteTarget] = useState('');
  const [orbisBots, setOrbisBots] = useState([]);
  const [availablePairs, setAvailablePairs] = useState([]);
  const [pairDropdown, setPairDropdown] = useState(null);
  const prevPnlRef = useRef({});
  const [pnlFlash, setPnlFlash] = useState({});
  const pollRef = useRef(null);

  const loadBots = useCallback(async () => {
    try {
      const [botsRes, stratRes, pairsRes] = await Promise.all([
        api.get('/test-bots'),
        api.get('/test-bots/strategies'),
        api.get('/test-bots/pairs'),
      ]);
      setBots(botsRes.data.bots || []);
      setStrategies(stratRes.data.strategies || []);
      setAvailablePairs(pairsRes.data.pairs || []);
    } catch (error) {
      console.error('Failed to load test bots', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Silent refresh — updates data without loading spinner
  const silentRefresh = useCallback(async () => {
    try {
      const res = await api.get('/test-bots');
      const newBots = res.data.bots || [];
      setBots(prev => {
        // Detect P&L changes for flash animation
        const flashes = {};
        newBots.forEach(nb => {
          if (nb.open_position) {
            const prevPnl = prevPnlRef.current[nb.id];
            const newPnl = nb.open_position.unrealized_pnl || 0;
            if (prevPnl !== undefined && prevPnl !== newPnl) {
              flashes[nb.id] = newPnl > prevPnl ? 'up' : 'down';
            }
            prevPnlRef.current[nb.id] = newPnl;
          } else {
            delete prevPnlRef.current[nb.id];
          }
        });
        if (Object.keys(flashes).length > 0) {
          setPnlFlash(flashes);
          setTimeout(() => setPnlFlash({}), 600);
        }
        return newBots;
      });
    } catch (error) {
      // Silently ignore — don't disrupt UI
    }
  }, []);

  useEffect(() => { loadBots(); }, [loadBots]);

  // Close pair dropdown when clicking outside
  useEffect(() => {
    if (!pairDropdown) return;
    const handler = () => setPairDropdown(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [pairDropdown]);

  // 3-second polling for live position updates
  useEffect(() => {
    pollRef.current = setInterval(silentRefresh, 3000);
    return () => clearInterval(pollRef.current);
  }, [silentRefresh]);

  const createAll = async () => {
    setCreating(true);
    try {
      const res = await api.post('/test-bots/create-all');
      toast.success(`Created ${res.data.created} test bots`);
      loadBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bots');
    } finally {
      setCreating(false);
    }
  };

  const startBot = async (id) => {
    try {
      await api.post(`/test-bots/${id}/start`);
      toast.success('Bot started');
      setBots(prev => prev.map(b => b.id === id ? { ...b, status: 'active' } : b));
    } catch (error) {
      toast.error('Failed to start bot');
    }
  };

  const stopBot = async (id) => {
    try {
      await api.post(`/test-bots/${id}/stop`);
      toast.success('Bot stopped');
      setBots(prev => prev.map(b => b.id === id ? { ...b, status: 'paused' } : b));
    } catch (error) {
      toast.error('Failed to stop bot');
    }
  };

  const restartBot = async (id) => {
    if (!window.confirm('Restart bot? This will reset all P&L and trades.')) return;
    try {
      await api.post(`/test-bots/${id}/restart`);
      toast.success('Bot restarted');
      loadBots();
    } catch (error) {
      toast.error('Failed to restart bot');
    }
  };

  const deleteBot = async (id) => {
    if (!window.confirm('Delete this test bot and all its trades?')) return;
    try {
      await api.delete(`/test-bots/${id}`);
      toast.success('Bot deleted');
      setBots(prev => prev.filter(b => b.id !== id));
    } catch (error) {
      toast.error('Failed to delete bot');
    }
  };

  const saveSettings = async () => {
    if (!settingsBot) return;
    try {
      await api.post(`/test-bots/${settingsBot}/settings`, settingsForm);
      toast.success('Settings saved');
      setSettingsBot(null);
      loadBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const openPromote = async (bot) => {
    setPromoteBot(bot);
    setPromoteName(bot.strategy_name || '');
    setPromoteTarget('');
    try {
      const res = await api.get('/test-bots/active-orbis-bots');
      setOrbisBots(res.data.bots || []);
    } catch (error) {
      setOrbisBots([]);
    }
  };

  const confirmPromote = async () => {
    if (!promoteBot) return;
    try {
      const payload = { orbis_name: promoteName || null };
      if (promoteTarget) payload.target_bot_id = promoteTarget;
      const res = await api.post(`/test-bots/${promoteBot.id}/promote`, payload);
      toast.success(res.data.message);
      setPromoteBot(null);
      setPromoteName('');
      setPromoteTarget('');
      loadBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote bot');
    }
  };

  const loadTrades = async (botId) => {
    if (botTrades[botId]) return;
    try {
      const res = await api.get(`/test-bots/${botId}/trades?limit=10`);
      setBotTrades(prev => ({ ...prev, [botId]: res.data.trades || [] }));
    } catch (error) {
      console.error('Failed to load trades');
    }
  };

  const changePair = async (botId, newPair) => {
    try {
      await api.post(`/test-bots/${botId}/change-pair`, { trading_pair: newPair });
      const pairInfo = availablePairs.find(p => p.id === newPair);
      toast.success(`Pair changed to ${pairInfo?.symbol || newPair}`);
      setBots(prev => prev.map(b => b.id === botId ? { ...b, trading_pair: newPair, trading_pair_symbol: pairInfo?.symbol } : b));
      setPairDropdown(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change pair');
    }
  };

  const forceClose = async (botId) => {
    if (!window.confirm('Force close this position at current market price?')) return;
    try {
      const res = await api.post(`/test-bots/${botId}/force-close`);
      const pnl = res.data.pnl;
      toast.success(`Position closed: ${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)} P&L`);
      loadBots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force close');
    }
  };

  const toggleExpand = (botId) => {
    if (expandedBot === botId) {
      setExpandedBot(null);
    } else {
      setExpandedBot(botId);
      loadTrades(botId);
    }
  };

  const openSettings = (bot) => {
    setSettingsBot(bot.id);
    setSettingsForm({
      max_daily_loss: bot.max_daily_loss || 5,
      stop_loss_percentage: bot.stop_loss_percentage || 2,
      risk_per_trade: bot.risk_per_trade || 0.01,
      risk_per_trade_display: ((bot.risk_per_trade || 0.01) * 100).toFixed(1),
    });
  };

  // Summary stats
  const totalBots = bots.length;
  const activeBots = bots.filter(b => b.status === 'active').length;
  const totalPnl = bots.reduce((s, b) => s + (b.paper_pnl || 0), 0);
  const totalTrades = bots.reduce((s, b) => s + (b.total_trades || 0), 0);
  const avgWinRate = totalBots > 0
    ? bots.reduce((s, b) => s + (b.win_rate || 0), 0) / totalBots
    : 0;

  if (loading) {
    return (
      <Card className="bg-[#15171C] border-[#1E293B]" data-testid="test-bot-folder-loading">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
          <span className="ml-3 text-[#94A3B8]">Loading test bots...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="test-bot-folder">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-500/15 to-indigo-500/10 border-purple-500/40">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
                <FlaskConical className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-['Outfit']" data-testid="test-bot-folder-title">Test Bot Folder</h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-[#94A3B8]">Strategy testing environment - {totalBots} bots, {activeBots} active</p>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30" data-testid="live-polling-indicator">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-[10px] text-green-400 font-medium">LIVE 3s</span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              onClick={createAll}
              disabled={creating}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold"
              data-testid="create-all-test-bots-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Deploy All Strategies
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {totalBots > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Bots', value: totalBots, color: 'text-purple-400' },
            { label: 'Active', value: activeBots, color: 'text-green-400' },
            { label: 'Total P&L', value: `£${totalPnl.toFixed(2)}`, color: totalPnl >= 0 ? 'text-green-400' : 'text-red-400' },
            { label: 'Trades', value: totalTrades, color: 'text-blue-400' },
            { label: 'Avg Win Rate', value: `${avgWinRate.toFixed(1)}%`, color: avgWinRate >= 50 ? 'text-green-400' : 'text-amber-400' },
          ].map((s, i) => (
            <Card key={i} className="bg-[#15171C] border-[#1E293B]">
              <CardContent className="py-4 px-4">
                <p className="text-xs text-[#94A3B8] mb-1">{s.label}</p>
                <p className={`text-lg font-bold font-['JetBrains_Mono'] ${s.color}`} data-testid={`test-summary-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bot Cards */}
      {bots.length === 0 ? (
        <Card className="bg-[#15171C] border-[#1E293B]">
          <CardContent className="text-center py-12">
            <FlaskConical className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
            <p className="text-[#94A3B8] text-lg mb-2">No test bots created yet</p>
            <p className="text-[#64748B] text-sm mb-6">Click "Deploy All Strategies" to create test bots for all available strategies</p>
            <Button onClick={createAll} disabled={creating} className="bg-purple-500 hover:bg-purple-600" data-testid="create-all-empty-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Deploy All Strategies
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bots.map((bot, idx) => (
            <motion.div
              key={bot.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <Card className={`bg-[#15171C] border transition-all ${
                bot.status === 'active' ? 'border-green-500/30' : 'border-[#1E293B]'
              }`} data-testid={`test-bot-card-${bot.id}`}>
                <CardContent className="py-4 px-5">
                  {/* Main Row */}
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    {/* Bot Info */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-10 rounded-full flex-shrink-0 ${bot.status === 'active' ? 'bg-green-500' : 'bg-[#1E293B]'}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm" data-testid={`test-bot-name-${bot.id}`}>{bot.name}</span>
                          <Badge className={`text-xs border ${RISK_COLORS[bot.risk_level] || RISK_COLORS.medium}`}>
                            {bot.risk_level}
                          </Badge>
                          <Badge className={`text-xs ${bot.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-[#1E293B] text-[#64748B]'}`}>
                            {bot.status?.toUpperCase()}
                          </Badge>
                          {bot.promoted && (
                            <Badge className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40">
                              <Trophy className="w-3 h-3 mr-1" /> PROMOTED
                            </Badge>
                          )}
                          {/* Trading Pair Badge */}
                          <div className="relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setPairDropdown(pairDropdown === bot.id ? null : bot.id); }}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border cursor-pointer transition-all hover:opacity-80 ${PAIR_COLORS[bot.trading_pair] || PAIR_COLORS.BTCUSD}`}
                              data-testid={`test-bot-pair-${bot.id}`}
                            >
                              <Coins className="w-3 h-3" />
                              {bot.trading_pair_symbol || 'BTC/USD'}
                            </button>
                            {pairDropdown === bot.id && (
                              <div className="absolute top-full left-0 mt-1 z-50 bg-[#15171C] border border-[#1E293B] rounded-lg shadow-xl py-1 min-w-[140px]" data-testid={`test-bot-pair-dropdown-${bot.id}`}>
                                {availablePairs.map(p => (
                                  <button
                                    key={p.id}
                                    onClick={(e) => { e.stopPropagation(); changePair(bot.id, p.id); }}
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#1E293B] transition-colors flex items-center gap-2 ${
                                      bot.trading_pair === p.id ? 'text-blue-400 font-semibold' : 'text-[#94A3B8]'
                                    }`}
                                    data-testid={`pair-option-${p.id}`}
                                  >
                                    <span className={`w-2 h-2 rounded-full ${bot.trading_pair === p.id ? 'bg-blue-400' : 'bg-[#1E293B]'}`} />
                                    {p.symbol}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-[#64748B] truncate">{bot.strategy_name} - {bot.description}</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 md:gap-6">
                      <div className="text-center">
                        <p className="text-xs text-[#94A3B8]">Trades</p>
                        <p className="font-bold font-['JetBrains_Mono'] text-sm" data-testid={`test-bot-trades-${bot.id}`}>{bot.total_trades || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-[#94A3B8]">P&L</p>
                        <p className={`font-bold font-['JetBrains_Mono'] text-sm ${(bot.paper_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid={`test-bot-pnl-${bot.id}`}>
                          {(bot.paper_pnl || 0) >= 0 ? '+' : ''}£{(bot.paper_pnl || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-[#94A3B8]">Win Rate</p>
                        <p className={`font-bold font-['JetBrains_Mono'] text-sm ${(bot.win_rate || 0) >= 50 ? 'text-green-400' : (bot.win_rate || 0) > 0 ? 'text-amber-400' : 'text-[#94A3B8]'}`} data-testid={`test-bot-winrate-${bot.id}`}>
                          {(bot.win_rate || 0).toFixed(1)}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-[#94A3B8]">Balance</p>
                        <p className="font-bold font-['JetBrains_Mono'] text-sm text-[#F5F7FA]">£{(bot.paper_balance || 0).toFixed(0)}</p>
                      </div>
                    </div>

                    {/* Live Open Position Display */}
                    {bot.open_position && (() => {
                      const pos = bot.open_position;
                      const pnl = pos.unrealized_pnl || 0;
                      const entry = pos.entry_price || 0;
                      const sl = pos.stop_loss || 0;
                      const tp = pos.take_profit || 0;
                      const current = pos.current_price || entry;
                      const isBuy = pos.side === 'buy';
                      const range = Math.abs(tp - sl);
                      const progress = range > 0 ? ((isBuy ? current - sl : sl - current) / range) * 100 : 50;
                      const clampedProgress = Math.max(0, Math.min(100, progress));
                      const flash = pnlFlash[bot.id];

                      return (
                        <div className={`w-full mt-3 p-2.5 rounded-lg border transition-all duration-300 ${
                          flash === 'up' ? 'bg-green-500/10 border-green-500/40' :
                          flash === 'down' ? 'bg-red-500/10 border-red-500/40' :
                          'bg-[#0B0D12] border-[#1E293B]'
                        }`} data-testid={`test-bot-position-${bot.id}`}>
                          {/* Single row: side + entry → current + P&L + close */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] px-1.5 py-0 ${isBuy ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {pos.side.toUpperCase()}
                              </Badge>
                              <span className="text-xs text-[#64748B] font-['JetBrains_Mono']">
                                {'\u00A3'}{entry.toLocaleString()} → {'\u00A3'}{current.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-['JetBrains_Mono'] text-sm font-bold ${
                                pnl >= 0 ? 'text-green-400' : 'text-red-400'
                              }`} data-testid={`test-bot-live-pnl-${bot.id}`}>
                                {pnl >= 0 ? '+' : ''}{'\u00A3'}{pnl.toFixed(2)}
                              </span>
                              <button
                                onClick={() => forceClose(bot.id)}
                                className="px-2 py-0.5 text-[10px] font-semibold rounded bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                                data-testid={`test-bot-force-close-${bot.id}`}
                              >
                                CLOSE
                              </button>
                            </div>
                          </div>

                          {/* Compact progress bar */}
                          <div className="relative h-1.5 rounded-full bg-[#1E293B] overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-red-500/30" style={{ width: '50%' }} />
                            <div className="absolute inset-y-0 right-0 bg-green-500/30" style={{ width: '50%' }} />
                            <div
                              className={`absolute top-[-1px] bottom-[-1px] w-2 rounded-full transition-all duration-500 ${
                                pnl >= 0 ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]' : 'bg-red-400 shadow-[0_0_4px_rgba(248,113,113,0.5)]'
                              }`}
                              style={{ left: `calc(${clampedProgress}% - 4px)` }}
                            />
                          </div>
                          <div className="flex justify-between mt-0.5 text-[9px] text-[#64748B] font-['JetBrains_Mono']">
                            <span>SL {'\u00A3'}{sl.toLocaleString()}</span>
                            <span>TP {'\u00A3'}{tp.toLocaleString()}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Actions */}
                    <div className="flex items-center gap-1.5">
                      {bot.status === 'active' ? (
                        <Button size="sm" variant="outline" onClick={() => stopBot(bot.id)} className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 h-8 px-2.5" data-testid={`test-bot-stop-${bot.id}`}>
                          <Square className="w-3.5 h-3.5 mr-1" /> Stop
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => startBot(bot.id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/40 h-8 px-2.5" data-testid={`test-bot-start-${bot.id}`}>
                          <Play className="w-3.5 h-3.5 mr-1" /> Start
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => restartBot(bot.id)} className="text-blue-400 hover:bg-blue-500/10 h-8 px-2" data-testid={`test-bot-restart-${bot.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openSettings(bot)} className="text-[#94A3B8] hover:text-[#F5F7FA] h-8 px-2" data-testid={`test-bot-settings-${bot.id}`}>
                        <Settings className="w-3.5 h-3.5" />
                      </Button>
                      {!bot.promoted && (
                        <Button size="sm" variant="ghost" onClick={() => openPromote(bot)} className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 px-2" data-testid={`test-bot-promote-${bot.id}`} title="Promote to Orbis">
                          <Trophy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteBot(bot.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2" data-testid={`test-bot-delete-${bot.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleExpand(bot.id)} className="text-[#94A3B8] h-8 px-2" data-testid={`test-bot-expand-${bot.id}`}>
                        {expandedBot === bot.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded: Recent Trades */}
                  <AnimatePresence>
                    {expandedBot === bot.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-[#1E293B]">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium">Recent Trades</span>
                            <span className="text-xs text-[#64748B]">Source: {bot.source}</span>
                          </div>
                          {(botTrades[bot.id] || []).length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs" data-testid={`test-bot-trades-table-${bot.id}`}>
                                <thead>
                                  <tr className="text-[#64748B]">
                                    <th className="text-left py-2 px-2">Time</th>
                                    <th className="text-left py-2 px-2">Side</th>
                                    <th className="text-right py-2 px-2">Price</th>
                                    <th className="text-right py-2 px-2">P&L</th>
                                    <th className="text-right py-2 px-2">Conf</th>
                                    <th className="text-left py-2 px-2">Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(botTrades[bot.id] || []).map((t, ti) => (
                                    <tr key={ti} className="border-t border-[#1E293B]/30">
                                      <td className="py-1.5 px-2 text-[#94A3B8]">{new Date(t.timestamp).toLocaleString()}</td>
                                      <td className="py-1.5 px-2">
                                        <span className={`font-semibold ${t.side === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                                          {t.side?.toUpperCase()}
                                        </span>
                                      </td>
                                      <td className="py-1.5 px-2 text-right font-['JetBrains_Mono'] text-[#F5F7FA]">£{t.price?.toFixed(2)}</td>
                                      <td className={`py-1.5 px-2 text-right font-['JetBrains_Mono'] font-semibold ${t.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {t.pnl >= 0 ? '+' : ''}£{t.pnl?.toFixed(2)}
                                      </td>
                                      <td className="py-1.5 px-2 text-right text-[#94A3B8]">{(t.confidence * 100)?.toFixed(0)}%</td>
                                      <td className="py-1.5 px-2 text-[#64748B] truncate max-w-[200px]">{t.reason}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-[#64748B] text-sm py-3 text-center">No trades yet — start the bot to begin testing</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Settings Modal */}
      <AnimatePresence>
        {settingsBot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setSettingsBot(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#15171C] border border-purple-500/30 rounded-xl p-6 w-full max-w-md mx-4"
              data-testid="test-bot-settings-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold text-lg">Risk Settings</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSettingsBot(null)} className="text-[#94A3B8]">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Max Daily Loss (%)</label>
                  <Input
                    type="number"
                    value={settingsForm.max_daily_loss === '' ? '' : settingsForm.max_daily_loss}
                    onChange={(e) => setSettingsForm(f => ({ ...f, max_daily_loss: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    onBlur={(e) => { if (e.target.value === '') setSettingsForm(f => ({ ...f, max_daily_loss: 5 })); }}
                    className="bg-[#0B0D12] border-[#1E293B]"
                    data-testid="settings-max-daily-loss"
                    min={1} max={20} step={0.5}
                  />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Stop Loss (%)</label>
                  <Input
                    type="number"
                    value={settingsForm.stop_loss_percentage === '' ? '' : settingsForm.stop_loss_percentage}
                    onChange={(e) => setSettingsForm(f => ({ ...f, stop_loss_percentage: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    onBlur={(e) => { if (e.target.value === '') setSettingsForm(f => ({ ...f, stop_loss_percentage: 2 })); }}
                    className="bg-[#0B0D12] border-[#1E293B]"
                    data-testid="settings-stop-loss"
                    min={0.5} max={10} step={0.5}
                  />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Risk Per Trade (%)</label>
                  <Input
                    type="number"
                    value={settingsForm.risk_per_trade_display === '' ? '' : settingsForm.risk_per_trade_display}
                    onChange={(e) => setSettingsForm(f => ({
                      ...f,
                      risk_per_trade_display: e.target.value === '' ? '' : e.target.value,
                      risk_per_trade: e.target.value === '' ? 0 : parseFloat(e.target.value) / 100
                    }))}
                    onBlur={(e) => { if (e.target.value === '') setSettingsForm(f => ({ ...f, risk_per_trade_display: '1.0', risk_per_trade: 0.01 })); }}
                    className="bg-[#0B0D12] border-[#1E293B]"
                    data-testid="settings-risk-per-trade"
                    min={0.1} max={5} step={0.1}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button onClick={() => setSettingsBot(null)} variant="outline" className="flex-1 border-[#1E293B]">Cancel</Button>
                <Button onClick={saveSettings} className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold" data-testid="save-settings-btn">
                  Save Settings
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promote Modal */}
      <AnimatePresence>
        {promoteBot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setPromoteBot(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#15171C] border border-amber-500/30 rounded-xl p-6 w-full max-w-md mx-4"
              data-testid="promote-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-400" />
                  <h3 className="font-semibold text-lg">Promote to Orbis</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPromoteBot(null)} className="text-[#94A3B8]">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-sm text-[#94A3B8] mb-4">
                Promote <span className="text-[#F5F7FA] font-semibold">{promoteBot.name}</span> to a production Orbis strategy available to all users.
              </p>

              {/* Stats summary */}
              <div className="grid grid-cols-3 gap-3 mb-5 p-3 rounded-lg bg-[#0B0D12] border border-[#1E293B]">
                <div className="text-center">
                  <p className="text-xs text-[#64748B]">Trades</p>
                  <p className="font-bold font-['JetBrains_Mono'] text-sm">{promoteBot.total_trades || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-[#64748B]">P&L</p>
                  <p className={`font-bold font-['JetBrains_Mono'] text-sm ${(promoteBot.paper_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(promoteBot.paper_pnl || 0) >= 0 ? '+' : ''}£{(promoteBot.paper_pnl || 0).toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-[#64748B]">Win Rate</p>
                  <p className={`font-bold font-['JetBrains_Mono'] text-sm ${(promoteBot.win_rate || 0) >= 50 ? 'text-green-400' : 'text-amber-400'}`}>
                    {(promoteBot.win_rate || 0).toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm text-[#94A3B8] mb-1 block">Orbis Strategy Name</label>
                <Input
                  value={promoteName}
                  onChange={(e) => setPromoteName(e.target.value)}
                  placeholder={promoteBot.strategy_name}
                  className="bg-[#0B0D12] border-[#1E293B]"
                  data-testid="promote-name-input"
                />
                <p className="text-xs text-[#64748B] mt-1">Will appear as "Orbis {promoteName || promoteBot.strategy_name}"</p>
              </div>

              {orbisBots.length > 0 && (
                <div className="mb-5">
                  <label className="text-sm text-[#94A3B8] mb-1 block">Assign to Active Orbis Bot (optional swap)</label>
                  <select
                    value={promoteTarget}
                    onChange={(e) => setPromoteTarget(e.target.value)}
                    className="w-full h-10 rounded-md bg-[#0B0D12] border border-[#1E293B] text-sm text-[#F5F7FA] px-3"
                    data-testid="promote-target-select"
                  >
                    <option value="">None — create as new strategy</option>
                    {orbisBots.map(ob => (
                      <option key={ob.id} value={ob.id}>
                        {ob.bot_name || ob.id} ({ob.strategy}) — {ob.status}
                      </option>
                    ))}
                  </select>
                  {promoteTarget && (
                    <p className="text-xs text-amber-400/80 mt-1">
                      The active bot's current strategy will be transferred to this test bot for continued testing.
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={() => setPromoteBot(null)} variant="outline" className="flex-1 border-[#1E293B]">Cancel</Button>
                <Button onClick={confirmPromote} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="confirm-promote-btn">
                  <Trophy className="w-4 h-4 mr-2" /> {promoteTarget ? 'Swap & Promote' : 'Promote'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TestBotFolder;
